import { useState } from 'react';
import { Activity, AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { EcogAssessment, Patient } from '../../../types';
import { Markdown } from '../../../components/Markdown/Markdown';
import { formatDisplayDate } from '../../../utils/displayDate';
import './ECOGSummary.scss';

interface ECOGSummaryProps {
  patient: Patient;
}

interface ECOGTrendPlotProps {
  data: EcogAssessment[];
}

/**
 * Render the interactive ECOG score history graph.
 *
 * @param props - Chronological ECOG assessments.
 * @returns The ECOG SVG trend graph.
 */
function ECOGTrendPlot({ data }: ECOGTrendPlotProps) {
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);

  if (data.length === 0) return null;

  const width = 800;
  const height = 320;
  const padding = { top: 40, right: 60, bottom: 60, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const minScore = -2;
  const maxScore = 5;

  const xScale = (index: number) => {
    return padding.left + (index / Math.max(data.length - 1, 1)) * chartWidth;
  };

  const yScale = (score: number) => {
    return padding.top + chartHeight - ((score - minScore) / (maxScore - minScore)) * chartHeight;
  };

  const getECOGColor = (score: number) => {
    if (score === 0) return '#10B981';
    if (score === 1) return '#3B82F6';
    if (score === 2) return '#F59E0B';
    if (score >= 3) return '#DC2626';
    return '#6B7280';
  };

  // Generate smooth curve path
  const generateSmoothPath = () => {
    if (data.length === 0) return '';

    const points = data.map((d, i) => ({
      x: xScale(i),
      y: yScale(d.score)
    }));

    if (points.length === 1) {
      return `M ${points[0].x},${points[0].y}`;
    }

    let path = `M ${points[0].x},${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const controlPointX = current.x + (next.x - current.x) / 2;
      path += ` C ${controlPointX},${current.y} ${controlPointX},${next.y} ${next.x},${next.y}`;
    }

    return path;
  };

  return (
    <div className="ecog-plot-container">
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="ecog-plot-svg">
        {/* Horizontal grid lines and labels */}
        {[0, 1, 2, 3, 4, 5].map((score) => {
          const y = yScale(score);
          return (
            <g key={score}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#E5E7EB"
                strokeWidth="1"
                strokeDasharray="4"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                fontSize="12"
                fill="#9CA3AF"
                textAnchor="end"
                fontFamily="Lato"
                fontWeight="600"
              >
                {score}
              </text>
            </g>
          );
        })}

        {/* Vertical grid lines */}
        {data.map((_, i) => {
          const x = xScale(i);
          return (
            <line
              key={`vgrid-${i}`}
              x1={x}
              y1={padding.top}
              x2={x}
              y2={height - padding.bottom}
              stroke="#E5E7EB"
              strokeWidth="1"
              strokeDasharray="4"
            />
          );
        })}

        {/* Smooth line connecting points */}
        <path
          d={generateSmoothPath()}
          fill="none"
          stroke="#3B82F6"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {data.map((d, i) => {
          const x = xScale(i);
          const y = yScale(d.score);
          const color = getECOGColor(d.score);

          return (
            <g key={i}>
              {/* Point circle */}
              <circle
                cx={x}
                cy={y}
                r={hoveredPoint === i ? 8 : 6}
                fill={color}
                stroke="white"
                strokeWidth="2"
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={() => setHoveredPoint(i)}
                onMouseLeave={() => setHoveredPoint(null)}
              />

              {/* Hover tooltip */}
              {hoveredPoint === i && (
                <g>
                  <rect
                    x={x - 85}
                    y={y - 85}
                    width="170"
                    height="70"
                    fill="white"
                    stroke="#D1D5DB"
                    strokeWidth="1.5"
                    rx="6"
                    style={{ filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))' }}
                  />
                  <text
                    x={x}
                    y={y - 60}
                    fontSize="14"
                    fontWeight="700"
                    textAnchor="middle"
                    fill="#111827"
                    fontFamily="Lato"
                  >
                    ECOG Score: {d.score}
                  </text>
                  <text
                    x={x}
                    y={y - 42}
                    fontSize="12"
                    textAnchor="middle"
                    fill="#6B7280"
                    fontFamily="Lato"
                    fontWeight="500"
                  >
                    {formatDisplayDate(d.date)}
                  </text>
                  <text
                    x={x}
                    y={y - 26}
                    fontSize="11"
                    textAnchor="middle"
                    fill="#6B7280"
                    fontFamily="Lato"
                  >
                    {d.assessor}
                  </text>
                </g>
              )}

              {/* Date labels below chart */}
              <text
                x={x}
                y={height - padding.bottom + 20}
                fontSize="11"
                fill="#6B7280"
                textAnchor="middle"
                fontFamily="Lato"
                fontWeight="500"
              >
                {formatDisplayDate(d.date)}
              </text>
            </g>
          );
        })}

        {/* Y-axis label */}
        <text
          x={padding.left - 45}
          y={height / 2}
          fontSize="13"
          fill="#6B7280"
          textAnchor="middle"
          fontFamily="Lato"
          fontWeight="600"
          transform={`rotate(-90, ${padding.left - 45}, ${height / 2})`}
        >
          ECOG Score
        </text>

        {/* X-axis label */}
        <text
          x={width / 2}
          y={height - 10}
          fontSize="13"
          fill="#6B7280"
          textAnchor="middle"
          fontFamily="Lato"
          fontWeight="600"
        >
          Assessment Date
        </text>
      </svg>
    </div>
  );
}

const ECOG_SCALE: Array<{ score: number; description: string }> = [
  { score: 0, description: 'Fully active, able to carry on all pre-disease performance without restriction' },
  { score: 1, description: 'Restricted in physically strenuous activity but ambulatory and able to do light work' },
  { score: 2, description: 'Ambulatory and capable of all self-care but unable to carry out any work; up >50% of waking hours' },
  { score: 3, description: 'Capable of only limited self-care; confined to bed or chair >50% of waking hours' },
  { score: 4, description: 'Completely disabled; cannot carry on any self-care; totally confined to bed or chair' },
];

/**
 * Render current ECOG status, historical graph, and the ECOG scale reference.
 *
 * @param props - Patient hydrated with a structured ECOG summary.
 * @returns The ECOG clinical summary.
 */
export function ECOGSummary({ patient }: ECOGSummaryProps) {
  const ecogData = patient.ecog;

  if (!ecogData) {
    return (
      <div className="ecog-report">
        <p style={{ color: 'var(--color-text-muted)' }}>No ECOG data available.</p>
      </div>
    );
  }

  const getECOGColor = (score: number) => {
    if (score === 0) return '#10B981'; // Green
    if (score === 1) return '#3B82F6'; // Blue
    if (score === 2) return '#F59E0B'; // Orange
    if (score >= 3) return '#DC2626'; // Red
    return '#6B7280'; // Gray
  };

  const getECOGLabel = (score: number) => {
    const labels: { [key: number]: string } = {
      0: 'Fully Active',
      1: 'Restricted',
      2: 'Ambulatory',
      3: 'Limited Self-Care',
      4: 'Disabled',
      5: 'Dead'
    };
    return labels[score] || 'Unknown';
  };

  // Calculate trend
  const getTrend = () => {
    if (ecogData.history.length < 2) return 'stable';
    const previousScore = ecogData.history[ecogData.history.length - 2].score;
    const currentScore = ecogData.currentECOG.score;

    if (currentScore < previousScore) return 'improving';
    if (currentScore > previousScore) return 'declining';
    return 'stable';
  };

  const trend = getTrend();
  const TrendIcon = trend === 'improving' ? TrendingUp : trend === 'declining' ? TrendingDown : Minus;

  return (
    <div className="ecog-report">
      {/* Header */}
      <div className="ecog-header">
        <div className="ecog-title">
          <Activity className="ecog-icon" />
          <h2 className="ecog-title-text">ECOG Performance Status</h2>
          <span className="ecog-subtitle">
            Latest: {formatDisplayDate(ecogData.documentDate)}
          </span>
        </div>
      </div>

      {/* Patient Recorded Disclaimer */}
      <div className="ecog-disclaimer">
        <AlertCircle size={16} />
        <span>These are Patient Recorded ECOG assessments</span>
      </div>

      {/* Current ECOG Status */}
      <div className="ecog-section">
        <h3 className="ecog-section-title">Current Status</h3>
        <div className="ecog-current-card">
          <div className="ecog-current-header">
            <div className="ecog-score-display">
              <div
                className="ecog-score-circle"
                style={{ backgroundColor: getECOGColor(ecogData.currentECOG.score) }}
              >
                {ecogData.currentECOG.score}
              </div>
              <div className="ecog-score-info">
                <h4 className="ecog-score-label">{getECOGLabel(ecogData.currentECOG.score)}</h4>
                <p className="ecog-score-date">{formatDisplayDate(ecogData.currentECOG.date)}</p>
              </div>
            </div>
            <div className={`ecog-trend-badge ecog-trend-${trend}`}>
              <TrendIcon size={16} />
              <span>{trend === 'improving' ? 'Improving' : trend === 'declining' ? 'Declining' : 'Stable'}</span>
            </div>
          </div>
          <div className="ecog-current-details">
            <div className="ecog-detail-row">
              <span className="ecog-detail-label">Assessed By:</span>
              <span className="ecog-detail-value">{ecogData.currentECOG.assessor}</span>
            </div>
            <div className="ecog-detail-row">
              <span className="ecog-detail-label">Clinical Notes:</span>
              <span className="ecog-detail-value">{ecogData.currentECOG.notes}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ECOG Trend Plot */}
      <div className="ecog-section">
        <h3 className="ecog-section-title">
          Historical Trend
          <span className="ecog-section-subtitle">({ecogData.history.length} assessments)</span>
        </h3>

        {/* Trend Plot */}
        <div className="ecog-trend-plot">
          <ECOGTrendPlot data={ecogData.history} />
        </div>
      </div>

      {/* Comparative trend summary (reduced narrative across assessments) */}
      {patient.narrative && (
        <div className="ecog-section">
          <h3 className="ecog-section-title">Trend Summary</h3>
          <Markdown className="ecog-narrative">{patient.narrative}</Markdown>
        </div>
      )}

      {/* ECOG Scale Reference */}
      <div className="ecog-section">
        <h3 className="ecog-section-title">ECOG Performance Scale Reference</h3>
        <div className="ecog-scale-table">
          {ECOG_SCALE.map((scale) => (
            <div key={scale.score} className="ecog-scale-row">
              <div
                className="ecog-scale-score"
                style={{ backgroundColor: getECOGColor(scale.score) }}
              >
                {scale.score}
              </div>
              <div className="ecog-scale-description">{scale.description}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
